import React, { useContext } from 'react';
import { StudentApiContext } from '../api/StudentApiContext';
import PageLayout from '../pagelayout';
import { LanguageContext } from '../i18n/LanguageStore';
import { useIntl } from 'react-intl';
import translations from './dashboard.student.i18n';
import * as css from './dashboard.module.scss';
import { todayAsFullDate } from '../date.utils';
import { Grid } from '@mui/material';
import Button from '../components/button';
import PanelHeader, {
  HeaderLevel,
} from '../components/panelheader/panelHeader';
import { useLoader } from '../api/useLoader';
import Spinner from '../components/spinner/spinner';
import { BrukerinfoDto } from '../api/generated/student-generated-api';
import Activites from './activities.student';
import { FakeLockedInput } from '../components/input';

const Dashboard = () => {
  // TODO: move this to a context?
  const { brukerApi } = useContext(StudentApiContext);
  const { data, loading, reload } = useLoader(() => brukerApi.brukerinfoGet());

  const { selectedLanguage } = useContext(LanguageContext);
  const { formatMessage } = useIntl();

  const saveConfirmation = () => {
    brukerApi
      .brukerinfoInnstillingerPut({
        brukerinnstillingerDto: {
          forstaatHvorProfildataKommerFra: true,
          spraakvalg: selectedLanguage,
        },
      })
      .then(() => {
        reload();
      });
  };

  const title: string = formatMessage({ id: translations.title.id });
  const welcomeMessage: string = formatMessage(
    { id: translations.welcome.id },
    { name: data ? data.fornavn + ' ' + data.etternavn : 'UKJENT' },
  );
  const desc: string = formatMessage({ id: translations.desc.id });

  return (
    <PageLayout isIndented={true} title={title}>
      {loading && <Spinner />}
      {data && (
        <div className={css.dashboard}>
          <div className={css.space65} />
          <div className={css.card}>
            <div>
              <p style={{ marginBottom: '2em' }}>
                {todayAsFullDate(selectedLanguage)}
              </p>
              <h2>{welcomeMessage}</h2>
              <p>{desc}</p>
              {!data.innstillinger.forstaatHvorProfildataKommerFra && (
                <StudentInfo data={data} onConfirm={saveConfirmation} />
              )}
            </div>
          </div>
          {data.innstillinger.forstaatHvorProfildataKommerFra && <Activites />}
        </div>
      )}
    </PageLayout>
  );
};

const StudentInfo: React.FC<
  React.PropsWithChildren<{ data: BrukerinfoDto; onConfirm: () => void }>
> = ({ data, onConfirm }) => {
  const { formatMessage } = useIntl();
  const labels = {
    addressinfo: formatMessage({ id: translations.addressinfo.id }),
    name: formatMessage({ id: translations.name.id }),
    studentnr: formatMessage({ id: translations.studentnr.id }),
    gender: formatMessage({ id: translations.gender.id }),
    age: formatMessage({ id: translations.age.id }),
    contact: formatMessage({ id: translations.contact.id }),
    street: formatMessage({ id: translations.street.id }),
    postcode: formatMessage({ id: translations.postcode.id }),
    posttown: formatMessage({ id: translations.posttown.id }),
    email: formatMessage({ id: translations.email.id }),
    telephone: formatMessage({ id: translations.telephone.id }),
    update: formatMessage({ id: translations.update.id }),
    understood: formatMessage({ id: translations.understood.id }),
  };

  return (
    <>
      <div className={css.space15} />
      <p>{labels.addressinfo}</p>
      <div className={css.space65} />
      <Grid container={true} spacing={1}>
        <Grid item={true} md={6} xs={12}>
          <FakeLockedInput
            id={'navn'}
            value={data.fornavn + ' ' + data.etternavn}
            label={labels.name}
          />
        </Grid>
        <Grid item={true} md={1} />
        <Grid item={true} md={2} xs={12}>
          <FakeLockedInput
            id={'studentnr'}
            value={data.studentnr ? data.studentnr : ''}
            label={labels.studentnr}
          />
        </Grid>
        <Grid item={true} md={2} sm={4} xs={8}>
          <FakeLockedInput
            id={'kjonn'}
            value={data.kjonn}
            label={labels.gender}
          />
        </Grid>
        <Grid item={true} md={1} sm={3} xs={4}>
          <FakeLockedInput
            id={'alder'}
            value={data.alder ? data.alder.toString() : ''}
            label={labels.age}
          />
        </Grid>
        <Grid item={true} md={1} sm={5} />
      </Grid>
      <PanelHeader
        headerLevel={HeaderLevel.h3}
        title={labels.contact}
        backgroundColor={'#ffffff'}
      />

      <Grid container={true} spacing={1}>
        <Grid item={true} md={6} xs={12}>
          <FakeLockedInput
            id={'gateadresse'}
            value={
              data.adresse.gateadresse != null ? data.adresse.gateadresse : ''
            }
            label={labels.street}
          />
        </Grid>
        <Grid item={true} md={1} />
        <Grid item={true} md={2} sm={4} xs={12}>
          <FakeLockedInput
            id={'postnr'}
            value={data.adresse.postnr != null ? data.adresse.postnr : ''}
            label={labels.postcode}
          />
        </Grid>
        <Grid item={true} md={3} sm={8} xs={12}>
          <FakeLockedInput
            id={'poststed'}
            value={data.adresse.poststed != null ? data.adresse.poststed : ''}
            label={labels.posttown}
          />
        </Grid>
        <Grid item={true} md={6} sm={8} xs={12}>
          <FakeLockedInput
            id={'epost'}
            value={data.epost != null ? data.epost : ''}
            label={labels.email}
          />
        </Grid>
        <Grid item={true} md={1} />
        <Grid item={true} md={3} sm={4} xs={12}>
          <FakeLockedInput
            id={'telefonnummer'}
            value={data.telefonnummer ? data.telefonnummer : ''}
            label={labels.telephone}
          />
        </Grid>
        <Grid item={true} md={2} />
      </Grid>
      <hr style={{ marginTop: '2em' }} />
      <p>{labels.update}</p>
      <div className={css.buttonContainer}>
        <Button.Secondary
          id={'confirmButton'}
          onClick={() => {
            onConfirm();
          }}
        >
          {labels.understood}
        </Button.Secondary>
      </div>
    </>
  );
};

export default Dashboard;
